import { useMemo } from "react";
import { MessageThreadType, SearchFilterType } from "gql/graphql";
import useMessageThreadFind from "./use-message-thread-find";
import { useAuth } from "modules/providers/auth-provider";

interface IProps {
  accessUserIdList?: string[];
}

const useChats = ({ accessUserIdList }: IProps) => {
  const { me } = useAuth();

  // serialize so the memoized filters (and therefore the query variables) are
  // stable across renders that pass an equal-but-new array
  const serializedUserIdList = useMemo(() => {
    if (!me || !accessUserIdList || accessUserIdList.length === 0) {
      return JSON.stringify([]);
    }
    return JSON.stringify([...new Set([me.id, ...accessUserIdList])].sort());
  }, [me, accessUserIdList]);

  const userIdList = useMemo(
    () => JSON.parse(serializedUserIdList) as string[],
    [serializedUserIdList],
  );

  // filter server-side by participants: the previous implementation fetched
  // only the first page of ALL chats and matched client-side, so any chat
  // older than the most recent DEFAULT_PAGE_SIZE chats was never found
  const searchFilters = useMemo(
    () => [
      {
        type: SearchFilterType.TypeList,
        stringListValue: [MessageThreadType.Chat],
      },
      {
        type: SearchFilterType.UserIdList,
        objectIdListValue: userIdList,
      },
    ],
    [userIdList],
  );

  const { messageThreads } = useMessageThreadFind({
    searchFilters,
    skip: userIdList.length === 0,
    // UserIdList matches chats CONTAINING all these users (supersets too), so
    // fetch the full narrowed set and pick the exact participant match below
    limit: false,
  });

  const chat = useMemo(() => {
    if (userIdList.length === 0) return undefined;
    return messageThreads.find(
      (messageThread) =>
        userIdList.length === messageThread.accessUserIdList.length &&
        userIdList.every((userId) =>
          messageThread.accessUserIdList.includes(userId),
        ),
    );
  }, [messageThreads, userIdList]);

  return {
    chat,
  };
};

export default useChats;
